import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LeftOutlined } from '@ant-design/icons';
import { Button, Drawer, Flex, Typography } from 'antd';

import { parseNegativeNumber } from '../../lib/parse-negative-number';
import { useGetTradingPlatformSessionCurrency } from '../../models/selectors/get-trading-platform-session-currency';
import type { CalendarData } from '../../models/types/transformed-calendar-data';
import { TradingPlatformDailyInfoAsDrawerNotes } from '../trading-platform-daily-info-as-drawer-notes/trading-platform-daily-info-as-drawer-notes';
import { TradingPlatformDailyInfoChart } from '../trading-platform-daily-info-chart/trading-platform-daily-info-chart';
import { TradingPlatformDailyInfoTitle } from '../trading-platform-daily-info-title/trading-platform-daily-info-title';
import { TradingPlatformTradeInfoCard } from '../trading-platform-trade-info-card/trading-platform-trade-info-card';

import cls from './trading-platform-daily-info-as-drawer.module.css';

interface TradingPlatformDailyInfoAsDrawerProps {
    isOpened: boolean;
    onClose: VoidFunction;
    data: Undefinable<CalendarData>;
}

export const TradingPlatformDailyInfoAsDrawer = memo(
    (props: TradingPlatformDailyInfoAsDrawerProps) => {
        const { isOpened, onClose, data } = props;

        const { t } = useTranslation('trading-journal');

        const currency = useGetTradingPlatformSessionCurrency();

        const { winningTrades, losingTrades, totalCommission, totalVolume } =
            useMemo(() => {
                if (!data?.trades) {
                    return {
                        losingTrades: 0,
                        totalCommission: 0,
                        totalVolume: 0,
                        winningTrades: 0,
                    };
                }

                const winning = data.trades.filter(
                    (trade) => trade.net_profit > 0,
                ).length;
                const losing = data.trades.filter(
                    (trade) => trade.net_profit <= 0,
                ).length;

                const commission = data.trades.reduce(
                    (sum, trade) =>
                        sum +
                        (trade.commission !== undefined &&
                        trade.commission !== null
                            ? trade.commission
                            : 0),
                    0,
                );

                const volume = data.trades.reduce(
                    (sum, trade) => sum + trade.volume,
                    0,
                );

                return {
                    losingTrades: losing,
                    totalCommission: commission,
                    totalVolume: volume,
                    winningTrades: winning,
                };
            }, [data]);

        if (!data) {
            return null;
        }

        const closeIcon = (
            <Button
                icon={<LeftOutlined />}
                onClick={onClose}
                size="small"
                type="text"
            />
        );

        return (
            <Drawer
                closeIcon={closeIcon}
                destroyOnClose
                height="85vh"
                onClose={onClose}
                open={isOpened}
                placement="bottom"
                rootClassName={cls.root}
                title={
                    <TradingPlatformDailyInfoTitle
                        dailyNetPL={data.total_profit}
                        date={data.date}
                    />
                }
            >
                <Flex vertical>
                    <div className={cls.chartContainer}>
                        <TradingPlatformDailyInfoChart
                            data={data.chartData}
                            isInsideDrawer
                        />
                    </div>

                    <Flex
                        className={cls.infoContainer}
                        gap={16}
                        vertical
                    >
                        <Flex
                            align="center"
                            justify="space-between"
                        >
                            <Typography.Text className={cls.title}>
                                {t('tradingJournalDailyInfoDataTotalProfit')}
                            </Typography.Text>
                            <Typography.Text className={cls.value}>
                                {parseNegativeNumber(
                                    data.total_profit,
                                    currency,
                                )}
                            </Typography.Text>
                        </Flex>

                        <Flex
                            align="center"
                            justify="space-between"
                        >
                            <Typography.Text className={cls.title}>
                                {t('tradingJournalDailyInfoDataWinRate')}
                            </Typography.Text>
                            <Typography.Text className={cls.value}>
                                {data.total_win_percent}%
                            </Typography.Text>
                        </Flex>

                        <Flex
                            align="center"
                            justify="space-between"
                        >
                            <Typography.Text className={cls.title}>
                                {t('tradingJournalDailyInfoDataProfitFactor')}
                            </Typography.Text>
                            <Typography.Text className={cls.value}>
                                {!data.profit_factor
                                    ? '--'
                                    : data.profit_factor.toFixed(2)}
                            </Typography.Text>
                        </Flex>

                        <Flex
                            align="center"
                            justify="space-between"
                        >
                            <Typography.Text className={cls.title}>
                                {t(
                                    'tradingJournalDailyInfoDataAverageWinTrade',
                                )}
                            </Typography.Text>
                            <Typography.Text className={cls.value}>
                                {parseNegativeNumber(
                                    data.average_win_trade,
                                    currency,
                                )}
                            </Typography.Text>
                        </Flex>

                        <Flex
                            align="center"
                            justify="space-between"
                        >
                            <Typography.Text className={cls.title}>
                                {t(
                                    'tradingJournalDailyInfoDataAverageLossTrade',
                                )}
                            </Typography.Text>
                            <Typography.Text className={cls.value}>
                                {parseNegativeNumber(
                                    data.average_loss_trade,
                                    currency,
                                )}
                            </Typography.Text>
                        </Flex>

                        <Flex
                            align="center"
                            justify="space-between"
                        >
                            <Typography.Text className={cls.title}>
                                {t('tradingJournalDailyInfoDataTradeCount')}
                            </Typography.Text>
                            <Typography.Text className={cls.value}>
                                {data.trades.length}
                            </Typography.Text>
                        </Flex>

                        <Flex
                            align="center"
                            justify="space-between"
                        >
                            <Typography.Text className={cls.title}>
                                {t('tradingJournalDailyInfoDataWinTradeCount')}
                            </Typography.Text>
                            <Typography.Text className={cls.value}>
                                {winningTrades}
                            </Typography.Text>
                        </Flex>

                        <Flex
                            align="center"
                            justify="space-between"
                        >
                            <Typography.Text className={cls.title}>
                                {t('tradingJournalDailyInfoDataLossTradeCount')}
                            </Typography.Text>
                            <Typography.Text className={cls.value}>
                                {losingTrades}
                            </Typography.Text>
                        </Flex>

                        <Flex
                            align="center"
                            justify="space-between"
                        >
                            <Typography.Text className={cls.title}>
                                {t('tradingJournalDailyInfoDataSumCommission')}
                            </Typography.Text>
                            <Typography.Text className={cls.value}>
                                {totalCommission === 0
                                    ? '--'
                                    : parseNegativeNumber(
                                          totalCommission,
                                          currency,
                                      )}
                            </Typography.Text>
                        </Flex>

                        <Flex
                            align="center"
                            justify="space-between"
                        >
                            <Typography.Text className={cls.title}>
                                {t('tradingJournalDailyInfoDataSumVolumeTotal')}
                            </Typography.Text>
                            <Typography.Text className={cls.value}>
                                {totalVolume === 0
                                    ? '--'
                                    : totalVolume.toFixed(2)}
                            </Typography.Text>
                        </Flex>
                    </Flex>

                    <TradingPlatformDailyInfoAsDrawerNotes
                        className={cls.buttonNotes}
                        date={data.date}
                    />

                    <Flex
                        className={cls.cardsContainer}
                        gap={16}
                        vertical
                    >
                        {data.trades.map((dataSource) => (
                            <TradingPlatformTradeInfoCard
                                key={dataSource.open_time}
                                data={dataSource}
                            />
                        ))}
                    </Flex>
                </Flex>
            </Drawer>
        );
    },
);

TradingPlatformDailyInfoAsDrawer.displayName =
    'TradingPlatformDailyInfoAsDrawer';
